import React, { useState, useMemo, useEffect, useCallback } from "react";
import "./CreatePlan.scss";
import "./GlobalCatalogManager.scss";
import useFetch from "../../../../components/useFetch";
import { AnalyticsIcon, BackIcon, CheckCircleIcon, DatasetIcon, GlobeAltIcon, HistoryIcon, LabelsIcon, ModelTrainingIcon, ResetIcon, TableIcon, TrustIcon, VersionsIcon } from "../../../Common/SvgIcons/WorkflowIcons";
import CustomFeatureModal from "./CustomFeatureModal";
import CustomResourceModal from "./CustomResourceModal";
import getDataFromApi from "../../../../components/getDataFromApi";
import Sidenav from "../../../navbar/Sidenav";
import { useLocation, useNavigate } from "react-router-dom";
import TitleHeader from "../../../project/header/TitleHeader";
import PathBreadcrumb from "../../../../components/PathBreadcrumb/PathBreadcrumb";
import { transform } from "lodash";
import { CheckIcon } from "lucide-react";

const extractApiErrorMessage = (error) => {
  const candidates = [
    error?.body?.message,
    error?.body?.detail,
    error?.response?.data?.message,
    error?.response?.data?.detail,
    error?.detail,
    error?.message,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof candidate === "object") {
      const nestedMessage = candidate.message || candidate.detail;
      if (nestedMessage) return String(nestedMessage);
      continue;
    }

    const rawMessage = String(candidate).replace(/^\d{3}:\s*/, "");
    const jsonStart = rawMessage.indexOf("{");

    if (jsonStart !== -1) {
      try {
        const parsed = JSON.parse(rawMessage.substring(jsonStart));
        const parsedMessage = parsed?.message || parsed?.detail;
        if (parsedMessage) return String(parsedMessage);
      } catch { }
    }

    if (rawMessage.trim()) return rawMessage.trim();
  }

  return "";
};

const isAssignedDeleteError = (message) => {
  const lowerMessage = String(message || "").toLowerCase();

  return (
    lowerMessage.includes("is used in") ||
    lowerMessage.includes("assigned to") ||
    lowerMessage.includes("used in plan") ||
    lowerMessage.includes("used by plan")
  );
};

const validateDeleteResponse = (response) => {
  const apiMessage = extractApiErrorMessage(response);

  if (isAssignedDeleteError(apiMessage)) {
    throw {
      type: "DELETE_BLOCKED",
      message: apiMessage,
      body: response,
    };
  }
};

const getDeleteErrorMessage = (error, itemType, fallback) => {
  const apiMessage = extractApiErrorMessage(error);

  if (isAssignedDeleteError(apiMessage)) {
    return `This ${itemType} is assigned to one or more active plans or templates. Remove it from those plans or templates before deleting it.`;
  }

  return fallback;
};

const getResourceMin = (resource) => {
  const min = Number(resource?.min ?? 0);
  return Number.isNaN(min) ? 0 : Math.max(0, min);
};

const getResourceMax = (resource) => {
  const min = getResourceMin(resource);
  const max = Number(resource?.max ?? 0);

  if (Number.isNaN(max) || max <= 0) return min;

  return Math.max(max, min);
};

const normalizeIdList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === "object") {
          return String(item.id ?? item._id ?? item.value ?? item.name ?? "");
        }

        return String(item ?? "");
      })
      .filter((item) => item.trim() !== "");
  }

  if (value === null || value === undefined) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        return normalizeIdList(JSON.parse(trimmed));
      } catch { }
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "object") {
    const id = value.id ?? value._id ?? value.value ?? value.name ?? "";
    return id === "" ? [] : [String(id)];
  }

  return [String(value)];
};

export default function CreatePlan() {


  const navigate = useNavigate();

  const location = useLocation();

  const planId = location?.state?.planId;
  const viewFrom = location?.state?.viewFrom;
  const planname = location?.state?.planName;



  const { data: planData, loading: planLoading, error: planError, refetch: refetchPlan } = useFetch(planId ? `forms/admin/create_plan?plan_id=${planId}` : "forms/admin/create_plan", "GET");

  const { data: featureData, loading: featureLoading, error: featureError, refetch: refetchFeatures } = useFetch(planId ? `admin/feature?filter=is_active=true&plan_id=${planId}&page=1&per_page=100` : "admin/feature?filter=is_active=true&page=1&per_page=100", "GET");

  const { data: resourceData, loading: resourceLoading, error: resourceError, refetch: refetchResources } = useFetch(planId ? `admin/resources?filter=is_active=true&plan_id=${planId}&page=1&per_page=100` : "admin/resources?filter=is_active=true&page=1&per_page=100", "GET");

  const role = localStorage.getItem("role");
  const isTenantAdmin = role === "tenant_admin";
  const isSystemAdmin = role === "system_admin";

  const refetchAllData = () => {
    refetchPlan?.();
    refetchFeatures?.();
    refetchResources?.();
  };



  const ENTITLEMENTS = useMemo(() => {
    const enumValues =
      planData?.json_schema?.properties?.["Feature Category"]?.enum || [];

    const enumNames =
      planData?.json_schema?.properties?.["Feature Category"]?.enumNames || [];

    const seen = new Set();

    return enumValues.reduce((acc, value, index) => {
      const id = String(value || "").trim().toLowerCase();
      if (!id || seen.has(id)) return acc;

      seen.add(id);
      acc.push({
        id,
        name: enumNames[index] || value,
      icon:
        id === "core" ? (
          <VersionsIcon className="wf-icon text-white" />
        ) : id === "data" ? (
          <DatasetIcon className="wf-icon text-white" />
        ) : id === "labeling" ? (
          <LabelsIcon className="wf-icon text-white" />
        ) : id === "training" ? (
          <AnalyticsIcon className="wf-icon text-white" />
        ) : id === "evaluation" ? (
          <HistoryIcon className="wf-icon text-white" />
        ) : id === "deployment" ? (
          <HistoryIcon className="wf-icon text-white" />
        ) : id === "solutions" ? (
          <TableIcon className="wf-icon text-white" />
        ) : id === "monitoring" ? (
          <ModelTrainingIcon className="wf-icon text-white" />
        ) : (
          <GlobeAltIcon className="wf-icon text-white" />
        )
      });

      return acc;
    }, []);
  }, [planData]);




  const BILLING_PERIODS = useMemo(() => {
    const enumValues =
      planData?.json_schema?.properties?.billing_cycle?.enum || [];

    const enumNames =
      planData?.json_schema?.properties?.billing_cycle?.enumNames || [];

    return enumValues.map((value, index) => ({
      id: value, // "month" | "year"
      label: enumNames[index] || value.toUpperCase(),
      months: value === "year" ? 12 : 1,
    }));
  }, [planData]);


  const FEATURES = useMemo(() => {
    const results = featureData?.results || [];
    return results.map((f) => {
      const category = (f.category || "").toLowerCase();
      return {
        id: f._id,
        name: f.name,
        description:
          f.description ||
          f.feature_description ||
          "",
        price: Number(f.base_price) || 0,
        category,
        entitlementId: category,
        core: category === "core",
        locked: !!f.is_core,
        isSelected: !!f.metadata?.is_selected,
        featureDependencies: normalizeIdList(
          f.metadata?.dependencies ??
          f.metadata?.["Feature Dependencies"] ??
          f.metadata?.feature_dependencies ??
          f["Feature Dependencies"] ??
          f.dependencies ??
          f.feature_dependencies
        ),
        resourceDependencies: normalizeIdList(
          f.metadata?.resource_dependencies ??
          f.metadata?.["Resource Dependencies"] ??
          f.resource_dependencies ??
          f["Resource Dependencies"] ??
          f.resources_dependencies
        ),
        raw: f,
      };
    });
  }, [featureData]);


  useEffect(() => {
    if (!FEATURES.length) return;

    const lockedIds = FEATURES.filter((f) => f.locked).map((f) => f.id);

    setFeatures((prev) => {
      // merge, don’t overwrite
      const set = new Set(prev);
      lockedIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  }, [FEATURES]);


  const featureCountByCategory = useMemo(() => {
    const map = {};
    FEATURES.forEach((f) => {
      if (f.category === "core") return;
      map[f.category] = (map[f.category] || 0) + 1;
    });
    return map;
  }, [FEATURES]);


  const FORM_FLAGS = useMemo(() => {
    const props = planData?.json_schema?.properties || {};
    return Object.entries(props)
      .filter(([_, def]) => def?.type === "boolean")
      .map(([key, def]) => ({
        key,
        label: (def?.title || key).replace(/^is_/, "").replace(/_/g, " "),
        defaultValue: def?.default ?? false,
      }));
  }, [planData]);


  const RESOURCES = useMemo(() => {
    const results = resourceData?.results || [];

    return results.map((r) => {
      const category = (r.category || "").toLowerCase();
      const metadata = r.metadata || {};
      const uiType = metadata.ui_type || r.ui_type || "input";

      return {
        id: r._id,
        name: r.name,
        unit: r.unit || "",
        pricePerUnit: Number(r.unit_price) || 0,
        core: category === "core",
        entitlementId: category === "core" ? null : category,
        type: uiType,
        min: metadata.min ?? 0,
        max: metadata.max ?? 0,
        scope: (r.resource_category || "tenant").toLowerCase(),
        currentValue: Number(metadata.current_value ?? 0),
        metadata,
        raw: r,
      };
    });
  }, [resourceData]);

  const [planName, setPlanName] = useState("");
  const [planType, setPlanType] = useState("");
  const [entitlements, setEntitlements] = useState(() => ENTITLEMENTS.filter((e) => e.id === "core" || e.isCore).map((e) => e.id));
  const [features, setFeatures] = useState([]);
  const [resources, setResources] = useState({});
  const [itemPriceAdjustments, setItemPriceAdjustments] = useState({
    features: {},
    resources: {},
  });
  const [formState, setFormState] = useState({});
  const [showCustomFeature, setShowCustomFeature] = useState(false);
  const [showCustomResource, setShowCustomResource] = useState(false);
  const [trialDurationDays, setTrialDurationDays] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [defaultsInitialized, setDefaultsInitialized] = useState(false);
  const [planSelectionsInitialized, setPlanSelectionsInitialized] = useState(false);
  const [editFeatureData, setEditFeatureData] = useState(null);
  const [editResourceData, setEditResourceData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, type: "", id: null, name: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);





  useEffect(() => {
    if (!planData?.json_schema?.properties) return;
    if (defaultsInitialized) return;

    const props = planData.json_schema.properties;

    // text / number / select defaults
    setPlanName(props?.name?.default ?? "");
    setPlanType(props?.plan_type?.default ?? "");
    setBillingPeriod(props?.billing_cycle?.default ?? "month");
    setTrialDurationDays(props?.["Trial Duration"]?.default ?? 15);

    // boolean defaults
    setFormState((prev) => {
      if (Object.keys(prev).length > 0) return prev;

      const next = {};

      Object.entries(props).forEach(([key, def]) => {
        if (def?.type === "boolean") {
          next[key] = def.default ?? false;
        }
      });

      if (isTenantAdmin) {
        next.is_public = false;
        next.is_template = false;
        next.is_active = true;
      }

      return next;
    });

    setDefaultsInitialized(true);
  }, [planData, defaultsInitialized, isTenantAdmin]);



  useEffect(() => {
    if (!planId) return;
    if (planSelectionsInitialized) return;
    if (!FEATURES.length && !RESOURCES.length) return;

    const selectedEntitlementIds = new Set(["core"]);
    const selectedFeatureIds = [];
    const resourceValues = {};

    FEATURES.forEach((f) => {
      if (f.isSelected) {
        selectedFeatureIds.push(f.id);

        if (f.entitlementId && f.entitlementId !== "core") {
          selectedEntitlementIds.add(f.entitlementId);
        }
      }
    });

    RESOURCES.forEach((r) => {
      if (r.currentValue > 0) {
        resourceValues[r.id] = r.currentValue;

        if (r.entitlementId) {
          selectedEntitlementIds.add(r.entitlementId);
        }
      }
    });

    setEntitlements(Array.from(selectedEntitlementIds));
    setFeatures(selectedFeatureIds);
    setResources(resourceValues);

    setPlanSelectionsInitialized(true);
  }, [planId, FEATURES, RESOURCES, planSelectionsInitialized]);



  /* ---------------- Visibility --------------- */

  // --- Visibility rules helpers ---
  const isTemplate = !!formState.is_template;
  const isPublic = !!formState.is_public;
  const isActive = !!formState.is_active;

  const setVisibilityMode = (mode) => {
    setFormState((prev) => {
      if (mode === "template") {
        return {
          ...prev,
          is_template: true,
          is_public: false,
          // active stays as-is (user can toggle)
        };
      }

      // mode === "public"
      return {
        ...prev,
        is_template: false,
        is_public: true,
        is_active: true, // force active
      };
    });
  };

  const toggleActive = (checked) => {
    // if public selected, active cannot be changed
    if (isPublic) return;

    setFormState((prev) => ({
      ...prev,
      is_active: checked,
    }));
  };

  // Optional safety: if somehow public becomes true, force active true
  useEffect(() => {
    if (formState.is_public && !formState.is_active) {
      setFormState((prev) => ({ ...prev, is_active: true }));
    }
  }, [formState.is_public]); // intentionally only watching is_public

  useEffect(() => {
    if (!FORM_FLAGS.length) return;

    setFormState((prev) => {
      const alreadyInitialized = Object.keys(prev).length > 0;
      if (alreadyInitialized) return prev;

      const next = {};
      FORM_FLAGS.forEach((f) => {
        next[f.key] = f.defaultValue;
      });

      if (isTenantAdmin) {
        next.is_public = false;
        next.is_template = false;
        next.is_active = true; // keep active, but private
      } else {
        next.is_public = true;
        next.is_template = false;
        next.is_active = true;
      }

      return next;
    });
  }, [FORM_FLAGS, isTenantAdmin]);

  // NEW: billing period selection (highlight + pricing)
  const [billingPeriod, setBillingPeriod] = useState("month");

  const selectedEntitlementsSet = useMemo(
    () => new Set(entitlements),
    [entitlements]
  );

  const selectedFeaturesSet = useMemo(
    () => new Set(features),
    [features]
  );

  // Only show core features + features that belong to selected entitlements
  const visibleFeatures = useMemo(() => {
    return FEATURES.filter((f) => {
      if (f.core) return true;
      return f.entitlementId && selectedEntitlementsSet.has(f.entitlementId);
    });
  }, [FEATURES, selectedEntitlementsSet]);

  const selectedFeatureResourceCategoryById = useMemo(() => {
    const resourceCategoryById = new Map();

    FEATURES.forEach((feature) => {
      const isFeatureVisible =
        feature.core ||
        (feature.entitlementId && selectedEntitlementsSet.has(feature.entitlementId));

      if (!isFeatureVisible) return;
      const isFeatureChecked = feature.locked || selectedFeaturesSet.has(feature.id);
      if (!isFeatureChecked) return;

      const displayCategory = feature.core ? "core" : feature.entitlementId;

      feature.resourceDependencies.forEach((resourceId) => {
        const key = String(resourceId);
        if (!resourceCategoryById.has(key)) {
          resourceCategoryById.set(key, displayCategory);
        }
      });
    });

    return resourceCategoryById;
  }, [FEATURES, selectedEntitlementsSet, selectedFeaturesSet]);

  const selectedFeatureResourceSourcesById = useMemo(() => {
    const sourcesByResourceId = new Map();

    FEATURES.forEach((feature) => {
      const isFeatureVisible =
        feature.core ||
        (feature.entitlementId && selectedEntitlementsSet.has(feature.entitlementId));

      if (!isFeatureVisible) return;
      const isFeatureChecked = feature.locked || selectedFeaturesSet.has(feature.id);
      if (!isFeatureChecked) return;

      const displayCategory = feature.core ? "core" : feature.entitlementId;

      feature.resourceDependencies.forEach((resourceId) => {
        const key = String(resourceId);
        const currentSources = sourcesByResourceId.get(key) || [];

        if (!currentSources.some((source) => source.id === feature.id)) {
          currentSources.push({
            id: feature.id,
            name: feature.name,
            entitlementId: displayCategory,
          });
        }

        sourcesByResourceId.set(key, currentSources);
      });
    });

    return sourcesByResourceId;
  }, [FEATURES, selectedEntitlementsSet, selectedFeaturesSet]);

  // Show resources only when they are linked to a checked feature.
  const visibleResources = useMemo(() => {
    return RESOURCES.reduce((acc, resource) => {
      const dependencyCategory = selectedFeatureResourceCategoryById.get(String(resource.id));

      if (dependencyCategory) {
        acc.push({
          ...resource,
          displayEntitlementId: dependencyCategory,
          sourceFeatures: selectedFeatureResourceSourcesById.get(String(resource.id)) || [],
        });
        return acc;
      }

      return acc;
    }, []);
  }, [RESOURCES, selectedFeatureResourceCategoryById, selectedFeatureResourceSourcesById]);

  // Grouping helper
  const groupItems = (items) => {
    const groups = { core: [], byEnt: {} };

    items.forEach((item) => {
      const groupKey = item.displayEntitlementId || (item.core ? "core" : item.entitlementId);

      if (groupKey === "core") {
        groups.core.push(item);
        return;
      }
      if (groupKey) {
        if (!groups.byEnt[groupKey]) groups.byEnt[groupKey] = [];
        groups.byEnt[groupKey].push(item);
      }
    });

    return groups;
  };

  const groupedFeatures = useMemo(() => groupItems(visibleFeatures), [visibleFeatures]);
  const groupedResources = useMemo(() => groupItems(visibleResources), [visibleResources]);

  /* ---------- HANDLERS ---------- */

  const getResourceValue = useCallback((resource) => {
    const rawValue = resources[resource.id];
    const min = getResourceMin(resource);

    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return resource.type === "slider" ? min : "";
    }

    const numericValue = Number(rawValue);

    if (Number.isNaN(numericValue)) {
      return resource.type === "slider" ? min : "";
    }

    if (resource.type !== "slider") return numericValue;

    return Math.min(Math.max(numericValue, min), getResourceMax(resource));
  }, [resources]);

  useEffect(() => {
    setResources((prev) => {
      const updated = { ...prev };

      visibleResources.forEach((r) => {
        const min = getResourceMin(r);
        const currentValue = Number(r.currentValue ?? 0);
        const defaultValue = currentValue > 0 ? Math.max(currentValue, min) : min;
        const storedValue = Number(updated[r.id]);
        const hasNoValue = updated[r.id] === null || updated[r.id] === undefined || updated[r.id] === "";
        const isBelowSliderMin = r.type === "slider" && !Number.isNaN(storedValue) && storedValue < min;

        if (hasNoValue || updated[r.id] === 0 || isBelowSliderMin) {
          if (defaultValue > 0 || r.type === "slider") {
            updated[r.id] = defaultValue;
          }
        }
      });

      return updated;
    });
  }, [visibleResources]);



  const coreEntitlementIds = useMemo(() => {
    return ENTITLEMENTS
      .filter((e) => e.id === "core")
      .map((e) => e.id);
  }, [ENTITLEMENTS]);

  const totalFeaturesByCategory = useMemo(() => {
    const map = {};
    FEATURES.forEach((f) => {
      map[f.category] = (map[f.category] || 0) + 1;
    });
    return map;
  }, [FEATURES]);

  useEffect(() => {
    if (confirmDelete.open) {
      setDeleteError(null);
    }
  }, [confirmDelete.open, confirmDelete.id, confirmDelete.type]);

  const selectedFeaturesByCategory = useMemo(() => {
    const map = {};
    FEATURES.forEach((f) => {
      if (!features.includes(f.id)) return;
      map[f.category] = (map[f.category] || 0) + 1;
    });
    return map;
  }, [FEATURES, features]);

  const [entitlementsInitialized, setEntitlementsInitialized] = useState(false);

  useEffect(() => {
    if (!coreEntitlementIds.length) return;
    if (entitlementsInitialized) return;

    setEntitlements(coreEntitlementIds);
    setEntitlementsInitialized(true);
  }, [coreEntitlementIds, entitlementsInitialized]);

  const toggleEntitlement = (id) => {
    // 🚫 core cannot be removed
    if (id === "core") return;

    setEntitlements((prev) =>
      prev.includes(id)
        ? prev.filter((e) => e !== id)
        : [...prev, id]
    );
  };


  const toggleFeature = (id) => {
    const f = FEATURES.find((x) => x.id === id);
    if (f?.locked) return; // 🚫 locked: do nothing

    setFeatures((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const updateResource = (id, value) => {
    setResources((prev) => ({
      ...prev,
      [id]: Number(value) || 0,
    }));
  };

  const updateItemPriceAdjustment = (itemType, id, changes) => {
    setItemPriceAdjustments((prev) => {
      const previousAdjustment = prev[itemType]?.[id] || {};
      const nextType = changes.type || previousAdjustment.type || "discount";
      const rawValue = Object.prototype.hasOwnProperty.call(changes, "value")
        ? changes.value
        : previousAdjustment.value;
      const maxValue = nextType === "discount" ? 100 : 1000;
      const normalizedValue =
        rawValue === ""
          ? ""
          : Math.min(Math.max(Number(rawValue) || 0, 0), maxValue);

      return {
        ...prev,
        [itemType]: {
          ...(prev[itemType] || {}),
          [id]: {
            type: nextType,
            value: normalizedValue,
          },
        },
      };
    });
  };

  const getItemPriceAdjustment = useCallback((itemType, id) => {
    const rawAdjustment = itemPriceAdjustments[itemType]?.[id];
    const type = rawAdjustment?.type === "margin" ? "margin" : "discount";
    const rawValue = typeof rawAdjustment === "object" ? rawAdjustment?.value : rawAdjustment;
    const numericValue = Number(rawValue);

    if (Number.isNaN(numericValue)) return { type, value: 0 };

    const maxValue = type === "discount" ? 100 : 1000;
    return {
      type,
      value: Math.min(Math.max(numericValue, 0), maxValue),
    };
  }, [itemPriceAdjustments]);

  const applyPriceAdjustment = useCallback((amount, itemType, id) => {
    const baseAmount = Number(amount) || 0;
    const adjustment = getItemPriceAdjustment(itemType, id);

    if (adjustment.type === "margin") {
      return baseAmount * (1 + adjustment.value / 100);
    }

    return baseAmount * (1 - adjustment.value / 100);
  }, [getItemPriceAdjustment]);

  const getFeaturePrice = useCallback(
    (feature) => applyPriceAdjustment(Number(feature?.price) || 0, "features", feature?.id),
    [applyPriceAdjustment]
  );

  const getResourceUnitPrice = useCallback(
    (resource) => applyPriceAdjustment(Number(resource?.pricePerUnit) || 0, "resources", resource?.id),
    [applyPriceAdjustment]
  );

  /* ---------- PRICE CALC ---------- */

  const monthlyPrice = useMemo(() => {
    const featurePrice = visibleFeatures
      .filter((f) => features.includes(f.id))
      .reduce((sum, f) => sum + getFeaturePrice(f), 0);

    const resourcePrice = visibleResources.reduce((sum, r) => {
      const value = Number(getResourceValue(r) || 0);
      return sum + value * getResourceUnitPrice(r);
    }, 0);

    // ❌ no base price anymore
    return featurePrice + resourcePrice;
  }, [visibleFeatures, visibleResources, features, getResourceValue, getFeaturePrice, getResourceUnitPrice]);

  const moduleCostBreakdown = useMemo(() => {
    return ENTITLEMENTS
      .filter((module) => entitlements.includes(module.id))
      .map((module) => {
        const moduleFeatures = visibleFeatures.filter((feature) => {
          const moduleId = feature.core ? "core" : feature.entitlementId;
          return moduleId === module.id && features.includes(feature.id);
        });

        const moduleResources = visibleResources.filter((resource) => {
          const moduleId =
            resource.displayEntitlementId ||
            (resource.core ? "core" : resource.entitlementId);
          return moduleId === module.id;
        });

        const featureCost = moduleFeatures.reduce(
          (sum, feature) => sum + getFeaturePrice(feature),
          0
        );

        const resourceCost = moduleResources.reduce((sum, resource) => {
          const value = Number(getResourceValue(resource) || 0);
          return sum + value * getResourceUnitPrice(resource);
        }, 0);

        return {
          ...module,
          featureCount: moduleFeatures.length,
          resourceCount: moduleResources.length,
          featureCost,
          resourceCost,
          totalCost: featureCost + resourceCost,
        };
      });
  }, [ENTITLEMENTS, entitlements, visibleFeatures, visibleResources, features, getResourceValue, getFeaturePrice, getResourceUnitPrice]);

  const activeResourceBreakdown = useMemo(() => {
    return visibleResources
      .map((resource) => {
        const value = Number(getResourceValue(resource) || 0);
        const cost = value * getResourceUnitPrice(resource);
        const moduleId =
          resource.displayEntitlementId ||
          (resource.core ? "core" : resource.entitlementId);
        const moduleName =
          ENTITLEMENTS.find((module) => module.id === moduleId)?.name || moduleId || "Core";

        return {
          id: resource.id,
          name: resource.name,
          unit: resource.unit,
          value,
          cost,
          adjustment: getItemPriceAdjustment("resources", resource.id),
          moduleName,
        };
      })
      .filter((resource) => resource.value > 0 || resource.cost > 0);
  }, [visibleResources, getResourceValue, getResourceUnitPrice, getItemPriceAdjustment, ENTITLEMENTS]);

  const selectedPeriod = useMemo(() => {
    return (
      BILLING_PERIODS.find((p) => p.id === billingPeriod) ||
      BILLING_PERIODS[0]
    );
  }, [billingPeriod, BILLING_PERIODS]);

  const getPeriodTotal = useCallback((period) => {
    const months = Number(period?.months || 1);
    return monthlyPrice * months;
  }, [monthlyPrice]);


  const handleResetAll = () => {
    const lockedIds = FEATURES.filter((f) => f.locked).map((f) => f.id);

    setEntitlements(coreEntitlementIds);
    setFeatures(lockedIds);     // keep locked selected
    setResources({});
    setItemPriceAdjustments({ features: {}, resources: {} });
    setFormState(() => {
      // start from schema defaults
      const next = FORM_FLAGS.reduce((acc, f) => {
        acc[f.key] = f.defaultValue;
        return acc;
      }, {});

      // ✅ enforce your business rule on reset too
      if (isTenantAdmin) {
        next.is_public = false;
        next.is_template = false;
        next.is_active = true;
      } else {
        next.is_public = true;
        next.is_template = false;
        next.is_active = true;
      }

      return next;
    });
    setBillingPeriod("month");
  };

  const periodTotal = useMemo(() => {
    return getPeriodTotal(selectedPeriod);
  }, [getPeriodTotal, selectedPeriod]);


  const zeroPriceWarningMessage =
    "The selected plan has a total price of 0. Please confirm that you want to create a free plan by clicking Create Plan again.";

  const emptyPlanWarningMessage =
    "This plan has no selected features and no resource allocation. Please confirm that you want to create an empty plan by clicking Create Plan again.";



  const selectedFeatureCount = useMemo(() => {
    return visibleFeatures.filter((f) => features.includes(f.id)).length;
  }, [visibleFeatures, features]);

  const totalResourceAllocation = useMemo(() => {
    return visibleResources.reduce((sum, r) => {
      return sum + Number(getResourceValue(r) || 0);
    }, 0);
  }, [visibleResources, getResourceValue]);

  const isEmptyPlan = selectedFeatureCount === 0 && totalResourceAllocation === 0;


  const validateForm = () => {
    if (!planName?.trim()) {
      setSubmitError("Plan name is required.");
      return false;
    }

    if (!isTenantAdmin && !planType?.trim()) {
      setSubmitError("Plan type is required.");
      return false;
    }

    if (!billingPeriod) {
      setSubmitError("Billing cycle is required.");
      return false;
    }

    return true;
  };

  const buildIncludedFeaturesByName = () => {
    const obj = {};
    visibleFeatures.forEach((f) => {
      if (!features.includes(f.id)) return;
      const adjustment = getItemPriceAdjustment("features", f.id);
      const basePrice = Number(f.price) || 0;
      const adjustedPrice = getFeaturePrice(f);

      obj[f.name] = {
        id: f.id,
        price: adjustedPrice,
        base_price: basePrice,
        discount_percent: adjustment.type === "discount" ? adjustment.value : 0,
        margin_percent: adjustment.type === "margin" ? adjustment.value : 0,
        price_adjustment_type: adjustment.value > 0 ? adjustment.type : "",
        is_core: !!f.locked,
        category: (f.category || "").toLowerCase(),
      };
    });
    return obj;
  };

  const buildResourcesByName = () => {
    const tenant_resources = {};
    const user_resources = {};

    visibleResources.forEach((r) => {
      const limit = Number(getResourceValue(r) || 0);
      const adjustment = getItemPriceAdjustment("resources", r.id);
      const baseUnitPrice = Number(r.pricePerUnit) || 0;
      const adjustedUnitPrice = getResourceUnitPrice(r);

      const category = (r.core
        ? "core"
        : (r.entitlementId || "")
      ).toLowerCase();

      const item = {
        id: r.id,
        limit,
        usage: 0,
        list_usage: [],
        unit: r.unit || "",
        unit_price: adjustedUnitPrice,
        base_unit_price: baseUnitPrice,
        discount_percent: adjustment.type === "discount" ? adjustment.value : 0,
        margin_percent: adjustment.type === "margin" ? adjustment.value : 0,
        price_adjustment_type: adjustment.value > 0 ? adjustment.type : "",
        category,
      };

      // ✅ CORRECT SCOPE CHECK
      if (r.scope === "user") {
        user_resources[r.name] = item;
      } else {
        tenant_resources[r.name] = item;
      }
    });

    return { tenant_resources, user_resources };
  };

  useEffect(() => {
    if (submitError === zeroPriceWarningMessage) {
      setSubmitError(null);
    }
  }, [monthlyPrice, billingPeriod, features, resources, itemPriceAdjustments, planName, planType]);

  const handleFinalizeOffer = () => {
    if (!validateForm()) return;

    if (isEmptyPlan && submitError !== emptyPlanWarningMessage) {
      setSubmitError(emptyPlanWarningMessage);
      return;
    }

    if (monthlyPrice === 0 && submitError !== zeroPriceWarningMessage) {
      setSubmitError(zeroPriceWarningMessage);
      return;
    }

    setSubmitLoading(true);
    setSubmitError(null);

    const included_features = buildIncludedFeaturesByName();
    const { tenant_resources, user_resources } = buildResourcesByName();

    const payload = {
      name: planName,
      plan_type: isTenantAdmin ? "enterprise" : planType,

      included_features,
      tenant_resources,
      user_resources,

      billing_cycle: billingPeriod,
      billing_price: Number((Math.round(periodTotal * 100) / 100).toFixed(2)),

      is_template: isTenantAdmin ? false : !!formState.is_template,
      is_active: !!formState.is_active,
      is_public: isTenantAdmin ? false : !!formState.is_public,

      "Trial Duration": isTenantAdmin ? 0 : Number(trialDurationDays),
    };

    getDataFromApi(
      `${window.ENV.API_URL}/forms/admin/create_plan`,
      "post",
      payload
    )
      .then(() => {
        setSubmitLoading(false);
        if (viewFrom === 'customplan') {
          navigate(-1)
        } else {
          navigate("/admin/plans-billing");
        }
      })
      .catch((e) => {
        setSubmitLoading(false);

        let message = "Failed to submit request. Please try again.";

        const detail =
          e?.response?.data?.detail ||
          e?.body?.detail ||
          e?.detail ||
          e?.message;

        if (detail) {
          try {
            const jsonPart = detail.substring(detail.indexOf("{"));
            const parsed = JSON.parse(jsonPart);

            message = parsed?.detail || message;
            message = message.replace(/^409:\s*/, "");
          } catch {
            message = detail.replace(/^\d{3}:\s*/, "");
          }
        }

        setSubmitError(message);
      });
  };

  const formatMoney = (value) => {
    const amount = Number(value) || 0;
    const rounded = Math.round(amount * 100) / 100;

    if (rounded === 0) return "0";
    if (Number.isInteger(rounded)) return rounded.toFixed(0);

    return rounded.toFixed(2);
  };




  const handleConfirmDelete = async () => {
    if (!confirmDelete?.id || !confirmDelete?.type) return;

    try {
      setDeleteLoading(true);
      setDeleteError(null);

      if (confirmDelete.type === "feature") {
        await handleDeleteFeature(confirmDelete.id);
      } else if (confirmDelete.type === "resource") {
        await handleDeleteResource(confirmDelete.id);
      }

      setDeleteError(null);
      setConfirmDelete({
        open: false,
        type: "",
        id: null,
        name: "",
      });
    } catch (e) {
      console.error("Delete confirmation failed:", e);
    } finally {
      setDeleteLoading(false);
    }
  };


  const handleDeleteFeature = async (featureId) => {
    try {
      const response = await getDataFromApi(
        `${window.ENV.API_URL}/admin/feature/${featureId}`,
        "delete"
      );
      validateDeleteResponse(response);

      setFeatures((prev) => prev.filter((id) => id !== featureId));
      refetchAllData?.();
    } catch (e) {
      console.error("Failed to delete feature:", e);
      const message = getDeleteErrorMessage(
        e,
        "feature",
        "Failed to delete feature. Please try again."
      );
      setDeleteError(message);
      throw e;
    }
  };

  const handleDeleteResource = async (resourceId) => {
    try {
      const response = await getDataFromApi(
        `${window.ENV.API_URL}/admin/resources/${resourceId}`,
        "delete"
      );
      validateDeleteResponse(response);

      setResources((prev) => {
        const updated = { ...prev };
        delete updated[resourceId];
        return updated;
      });

      refetchAllData?.();
    } catch (e) {
      console.error("Failed to delete resource:", e);
      const message = getDeleteErrorMessage(
        e,
        "resource",
        "Failed to delete resource. Please try again."
      );
      setDeleteError(message);
      throw e;
    }
  };

  const isDeleteBlocked = Boolean(
    deleteError && (
      deleteError.toLowerCase().includes("assigned to") ||
      isAssignedDeleteError(deleteError)
    )
  );

  const goToFeatureManager = (moduleId) => {
    navigate("/admin/features", { state: { moduleId, fromCreatePlan: true } });
  };

  const goToResourceManager = (moduleId) => {
    navigate("/admin/resources", { state: { moduleId, fromCreatePlan: true } });
  };

  return (
    <>

      <div className="mainmenu projects-content de-width">

        <PathBreadcrumb
          onBack={() => {
            if (viewFrom === 'customplan') {
              navigate(-1)
            } if (viewFrom === 'createplan') {
              navigate('/admin')
            } else {
              navigate("/admin/plans-billing");
            }
          }}
          items={
            viewFrom === 'createplan'
              ? [
                  { label: 'Admin', onClick: () => navigate('/admin') },
                  { label: 'Create Plan' },
                ]
              : viewFrom === 'template'
                ? [
                    { label: 'Admin', onClick: () => navigate('/admin') },
                    { label: 'Plans', onClick: () => navigate('/admin/plans-billing') },
                    { label: planname || 'Plan', onClick: () => navigate(-1) },
                    { label: 'Custom Offering' },
                  ]
                : [
                    { label: 'Admin', onClick: () => navigate('/admin') },
                    { label: 'Plans', onClick: () => navigate('/admin/plans-billing') },
                    { label: 'Custom Offering' },
                  ]
          }
        />


        <div className={`d-a justify-content-between pt-1 gap-3`}>
          <TitleHeader Icon={TrustIcon} title1={'Design your'} title2={' Custom Offering'} subtext={'Customize specific modules, resources, and pricing tiers for your enterprise customers.'} />
          <button className="resetBtn  wf-icon-red text-secondary text-nowrap border-0 bg-transparent py-0" type="button" onClick={handleResetAll} style={{ width: 'fit-content', height: 'unset' }}>
            <ResetIcon className="wf-icon restBtn-icon py-0" /> Reset all
          </button>
        </div>


        {!isTenantAdmin && (
          <div className="container-card global-architecture-panel mt-4">
            <div className="global-architecture-copy">
              <span className="global-architecture-dot"></span>
              <span>
                <strong className="text-primary">Global Resource Architecture Active:</strong>{" "}
               <small className="fw-light"> Create global variables under Resources, then bind them as dependencies inside Features.</small>
              </span>
            </div>
            <button
              className="plain-button2 text-primary fw-bold text-nowrap"
              onClick={() => goToResourceManager("core")}
            >
              Go Manage Global Resources <BackIcon className="wf-icon-small text-primary" style={{transform:'rotate(141deg)'}} />
            </button>
          </div>
        )}



        <div className='d-flex gap-5 admin-cp-content mt-5'>


          <div className='admin-content-cp-card1 flex-fill'>
            {isTenantAdmin && (
              <div className=" mb-3">
                <input
                  className="badge-border text container-card mt-1 w-100 p-3"
                  style={{ outline: "none" }}
                  placeholder="Plan Name   e.g., Q3 Enterprise Alpha"
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                />
              </div>)}



            {!isTenantAdmin && (
              <div className="container-card mb-4  position-relative overflow-hidden">
                <div>
                  <div className="d-a gap-2">
                    <TrustIcon className="wf-icon text-primary" />
                    <h6 className="m-0 fw-bold">Plan Specifications & Metadata</h6>
                  </div>
                  <small className="text-secondary">
                    Configure core plan identities, training limits, and deployment environment status.
                    These fields are compiled dynamically into the finalized plan recipe.
                  </small>
                </div>


                <div className="row mt-4">
                  <div className="col-12 col-md-6 mb-3">
                    <span className="fw-semibold">Plan Name</span>
                    <input
                      className="badge-border text container-card mt-1 w-100 p-3"
                      style={{ outline: "none" }}
                      placeholder="e.g., Q3 Enterprise Alpha"
                      value={planName}
                      onChange={(e) => setPlanName(e.target.value)}
                    />
                  </div>

                  <div className="col-12 col-md-6 mb-3">
                    <span className="fw-semibold">Plan Type</span>
                    <input
                      className="badge-border text container-card mt-1 w-100 p-3"
                      style={{ outline: "none" }}
                      placeholder="e.g., Enterprise"
                      value={planType}
                      onChange={(e) => setPlanType(e.target.value)}
                    />
                  </div>

                  <div className="col-12 col-md-6 mb-3">
                    <span className="fw-semibold">Trial Duration (Days)</span>
                    <input
                      type="number"
                      min={0}
                      max={30}
                      className="badge-border text container-card mt-1 w-100 p-3"
                      value={trialDurationDays}
                      onChange={(e) => {
                        let value = Number(e.target.value);
                        if (value < 1) value = 0;
                        if (value > 30) value = 30;
                        setTrialDurationDays(value);
                      }}
                    />
                  </div>

                  <div className="col-12 col-md-6 mb-3">
                    <span className="fw-semibold">Governance &amp; Visibility</span>

                    <div className="container-card mt-2 d-a p-0" style={{ background: 'var(--card-color)' }}>
                      <button
                        type="button"
                        className={` flex-fill container-card ${isTemplate ? "is-active text" : " text-secondary bg-transparent border-0 shadow-none"}`}
                        style={{ padding: '11px 0px' }}
                        onClick={() => setVisibilityMode("template")}
                      >
                        <CheckIcon className={isTemplate ? "text-primary wf-icon-small me-2" : " wf-icon-small me-2 text-secondary"} />
                        Template
                      </button>

                      <button
                        type="button"
                        className={` flex-fill container-card ${isPublic ? "is-active text" : " text-secondary bg-transparent border-0 shadow-none"}`}
                        style={{ padding: '11px 0px' }}
                        onClick={() => setVisibilityMode("public")}
                      >
                        <CheckIcon className={isPublic ? "text-primary wf-icon-small me-2" : " wf-icon-small me-2 text-secondary"} />
                        Public
                      </button>
                    </div>

                    <div className=" mx-2 mt-3">
                      <div className={`d-flex align-items-center justify-content-between text-secondary badge-border wf-icon-yellow ${isActive ? 'wf-icon-blue' : 'bg-transparent'}`} style={{ width: 'unset', height: 'unset' }}>
                        <div className="d-flex align-items-start gap-2" style={{ width: 'calc(100% - 50px)' }}>
                          <GlobeAltIcon className={`wf-icon-large p-2 badge-border ${isActive && 'bg-primary text-white'}`} />
                          <div>
                            <div className={`fw-semibold`} style={{ lineHeight: 1.1 }}>
                              STATUS: <span className={`fw-semibold ${isActive && 'text-primary'} `}>{isActive ? "ACTIVE" : "INACTIVE"}</span>
                            </div>
                            <div className="text-secondary small">
                              {isPublic
                                ? "Public plan is live and ready for deployment."
                                : isTemplate && isActive
                                  ? "Template is active and available for internal use (not customer-visible)."
                                  : isTemplate && !isActive
                                    ? "Template is inactive — enable it to use internally."
                                    : !isTemplate && isActive
                                      ? "Plan is active but private — ready to be assigned to customers."
                                      : "Plan is inactive — enable it to deploy or assign."}
                            </div>
                          </div>
                        </div>

                        <label className="cpSwitch">
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => toggleActive(e.target.checked)}
                            disabled={isPublic}
                          />
                          <span className="cpSwitch__slider" />
                        </label>
                      </div>

                      <div className="text-secondary small mt-2 text-center">
                        * Template and Public modes are mutually exclusive.
                      </div>
                    </div>
                  </div>

                </div>
                <div className="decor-circle-cp bg-success"></div>
              </div>)}



            <div className="container-card mb-4 position-relative overflow-hidden cpModulePanel">
              <div className="d-a justify-content-between gap-3 flex-wrap">
                <div>
                  <h6 className="m-0 fw-bold">Select Modules</h6>
                  <small className="text-secondary m-0 mt-1">
                    Select multiple modules to build a comprehensive plan. Customize each module's capabilities below.
                  </small>
                </div>

                <span className="cpModuleSelectedPill text-nowrap">
                  Selected Modules: {entitlements?.length}
                </span>
              </div>

              {/* ENTITLEMENTS */}
              <div className="cpModuleGrid mt-4">
                {ENTITLEMENTS.map((item) => {
                  const count = featureCountByCategory[item.id] || 0;

                  return (
                    <div key={item.id} className="cpModuleGrid__item">
                      <div
                        className={`plansBilling__entitlementCard cpModuleCard ${entitlements.includes(item.id)
                          ? "plansBilling__entitlementCard--active active"
                          : ""
                          }`}
                        onClick={() => toggleEntitlement(item.id)}
                      >
                        <span className="cpModuleCheck">
                          {entitlements.includes(item.id) && <CheckIcon className="wf-icon-small text-white" />}
                        </span>

                        <div className="d-a gap-3">
                          <span className="plansBilling__entitlementIcon d-j-a">
                            {item.icon}
                          </span>

                          <div className="d-flex flex-column flex-grow-1 gap-1">
                            <span className="cpModuleName">
                              {item.name}
                            </span>
                            {/* <small className="cpModuleActiveText">
                              {selected} Feature{selected !== 1 ? "s" : ""} Active
                            </small> */}
                            <small className="text-secondary">
                              {count} Features available
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="decor-circle-cp bg-primary"></div>
            </div>





            {ENTITLEMENTS
              .filter((ent) => ent.id !== "core") // optional (core already handled above)
              .map((ent) => {
                const isSelected = entitlements.includes(ent.id); // module selected?
                if (!isSelected) return null; // only show container for selected modules

                const featureItems = groupedFeatures.byEnt[ent.id] || [];
                const resourceItems = groupedResources.byEnt[ent.id] || [];

                const hasFeatures = featureItems.length > 0;
                const hasResources = resourceItems.length > 0;
                const checkedFeatureItems = featureItems.filter(
                  (feature) => feature.locked || features.includes(feature.id)
                );
                const resourceGroups = checkedFeatureItems
                  .map((feature) => ({
                    feature,
                    resources: resourceItems.filter((resource) =>
                      (resource.sourceFeatures || []).some((source) => source.id === feature.id)
                    ),
                  }))
                  .filter((group) => group.resources.length > 0);
                const groupedResourceIds = new Set(
                  resourceGroups.flatMap((group) => group.resources.map((resource) => resource.id))
                );
                const ungroupedResources = resourceItems.filter(
                  (resource) => !groupedResourceIds.has(resource.id)
                );
                const renderResourceCard = (resource, keySuffix = "") => {
                  const adjustment = getItemPriceAdjustment("resources", resource.id);
                  const sourceNames = (resource.sourceFeatures || []).map((source) => source.name);

                  return (
                    <div key={`${resource.id}${keySuffix}`} className="container-card p-3 mt-2 cpFeatureRow plansBilling__resourceCard">
                      <div className="plansBilling__resourceHeader">
                        <div className="d-a gap-2">
                          <span>{resource.name}</span>
                        </div>
                        {resource.type === "slider" &&
                          <span className="plansBilling__resourceValue">
                            {resource.type === "slider"
                              ? `${getResourceValue(resource)} / ${getResourceMax(resource)}`
                              : (resource.max ?? 0)} {resource.unit}
                          </span>}
                      </div>

                      {sourceNames.length > 0 && (
                        <div className="plansBilling__resourceSource">
                          Required by {sourceNames.join(", ")}
                        </div>
                      )}

                      {resource.type === "slider" ? (
                        <>
                          <input
                            type="range"
                            min={getResourceMin(resource)}
                            max={getResourceMax(resource)}
                            value={getResourceValue(resource)}
                            onChange={(e) =>
                              updateResource(resource.id, e.target.value)
                            }
                            className="plansBilling__resourceSlider"
                          />
                          <div className="plansBilling__resourceRangeMeta">
                            <span>Min {getResourceMin(resource)} {resource.unit}</span>
                            <span>Selected {getResourceValue(resource)} {resource.unit}</span>
                            <span>Max {getResourceMax(resource)} {resource.unit}</span>
                          </div>
                        </>
                      ) : (
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder={`0 ${resource.unit}`}
                          value={resources[resource.id] ?? ""}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^0-9]/g, "");
                            updateResource(resource.id, cleaned === "" ? "" : cleaned);
                          }}
                          className="plansBilling__resourceInput"
                        />
                      )}

                      <div className="plansBilling__resourceUnitPrice">
                        <span>${formatMoney(getResourceUnitPrice(resource))} / {resource.unit}</span>
                        {adjustment.value > 0 && (
                          <small>
                            {adjustment.type === "discount" ? "was" : "base"} ${formatMoney(Number(resource.pricePerUnit) || 0)} / {resource.unit}
                          </small>
                        )}
                      </div>
                      {isSystemAdmin && (
                        <PriceAdjustmentControl
                          type={adjustment.type}
                          value={itemPriceAdjustments.resources?.[resource.id]?.value ?? ""}
                          onTypeChange={(type) => updateItemPriceAdjustment("resources", resource.id, { type })}
                          onValueChange={(value) => updateItemPriceAdjustment("resources", resource.id, { value })}
                        />
                      )}
                    </div>
                  );
                };

                return (
                  <div key={ent.id} className="container-card mb-4 position-relative overflow-hidden">
                    <div className="mb-2">
                      <h6 className="mb-3">{ent.name} – Features & Resources</h6>

                      <div className="row mt-4">
                        {/* FEATURES */}
                        <div className="col-md-6 mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <span
                              className="fw-bold text-secondary mt-2"
                              style={{ fontSize: "10px" }}
                            >
                              INCLUDED FEATURES
                            </span>
                            {/* {!isTenantAdmin && (
                              <button
                                type="button"
                                style={{ zIndex: '2' }}
                                className="module-manage-link"
                                onClick={() => goToFeatureManager(ent.id)}
                              >
                                Manage Features
                              </button>)} */}
                          </div>

                          <div style={{ maxHeight: '500px', overflow: 'auto', scrollbarWidth: 'thin' }}>
                            {hasFeatures ? (
                              featureItems.map((feature) => {
                                const adjustment = getItemPriceAdjustment("features", feature.id);

                                return (
                                  <div key={feature.id} className="container-card mt-2 cpFeatureRow">
                                    <label className="d-a gap-2">
                                      <input
                                        type="checkbox"
                                        className="plansBilling__featureCheckbox"
                                        checked={feature.locked || features.includes(feature.id)}
                                        onChange={() => toggleFeature(feature.id)}
                                      />
                                      <span>{feature.name}</span>
                                      <span className="plansBilling__featurePrice">
                                        <span>${formatMoney(getFeaturePrice(feature))}</span>
                                        {adjustment.value > 0 && (
                                          <small>
                                            {adjustment.type === "discount" ? "was" : "base"} ${formatMoney(Number(feature.price) || 0)}
                                          </small>
                                        )}
                                      </span>
                                    </label>
                                    {isSystemAdmin && (
                                      <PriceAdjustmentControl
                                        type={adjustment.type}
                                        value={itemPriceAdjustments.features?.[feature.id]?.value ?? ""}
                                        onTypeChange={(type) => updateItemPriceAdjustment("features", feature.id, { type })}
                                        onValueChange={(value) => updateItemPriceAdjustment("features", feature.id, { value })}
                                      />
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <div className="container-card mt-2 p-3 text-secondary small">
                                <div>No features added for <strong>{ent.name}</strong> yet.</div>
                                {!isTenantAdmin && (
                                  <button
                                    type="button"
                                    className="plansBilling__emptyCreateButton mt-3"
                                    onClick={() => goToFeatureManager(ent.id)}
                                  >
                                    Create Feature
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* RESOURCES */}
                        <div className="col-md-6 mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <span
                              className="fw-bold text-secondary"
                              style={{ fontSize: "10px" }}
                            >
                              RESOURCE ALLOCATIONS
                            </span>
                            {/* {!isTenantAdmin && (
                              <button
                                type="button"
                                style={{ zIndex: '2' }}
                                className="module-manage-link"
                                onClick={() => goToResourceManager(ent.id)}
                              >
                                Manage Resources
                              </button>)} */}
                          </div>


                          <div style={{ maxHeight: '500px', overflow: 'auto', scrollbarWidth: 'thin' }}>
                            {hasResources ? (
                              <>
                                {resourceGroups.map((group) => (
                                  <div key={group.feature.id} className="plansBilling__resourceFeatureGroup">
                                    <div className="plansBilling__resourceFeatureHead">
                                      <span>Resources for</span>
                                      <strong>{group.feature.name}</strong>
                                    </div>
                                    {group.resources.map((resource) =>
                                      renderResourceCard(resource, `-${group.feature.id}`)
                                    )}
                                  </div>
                                ))}

                                {ungroupedResources.length > 0 && (
                                  <div className="plansBilling__resourceFeatureGroup">
                                    <div className="plansBilling__resourceFeatureHead">
                                      <span>Resources</span>
                                      <strong>General allocation</strong>
                                    </div>
                                    {ungroupedResources.map((resource) => renderResourceCard(resource))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="container-card mt-2 p-3 text-secondary small">
                                <div>Check a feature with resource dependencies to configure allocations for <strong>{ent.name}</strong>.</div>
                                {/* {!isTenantAdmin && (
                                  <button
                                    type="button"
                                    className="plansBilling__emptyCreateButton mt-3"
                                    onClick={() => goToResourceManager(ent.id)}
                                  >
                                    Create Resource
                                  </button>
                                )} */}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Optional: if BOTH empty, show one combined message too */}
                      {/* {!hasFeatures && !hasResources && (
                        <div className="container-card mt-2 p-3 text-secondary small">
                          This module is selected, but nothing is configured yet. Add features
                          and/or resources to include it in this plan.
                        </div>
                      )} */}
                    </div>

                    <div className="decor-circle-cp bg-danger"></div>
                  </div>
                );
              })}




          </div>

          <div className='admin-content-cp-card2'>


            {/* ================= RIGHT PANEL ================= */}
            <div className="plansBilling__rightPanel container-card bg-main-container ">
              <div className="pbSummary pbBreakdown">

                <h6 className="fw-bold text-white m-0">PRICING SUMMARY</h6>

                <div className="pbSummary__divider"></div>


                {/* Blueprint Metadata */}
                <div className="container-card bg-transparent p-3 my-4 border-metadata">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-secondary text-uppercase fw-bold small">
                      Blueprint Metadata
                    </span>
                  </div>

                  <div className="pbSummary__divider my-2"></div>

                  <div className="d-flex justify-content-between gap-3 mb-2">
                    <small className=" meta-sub-title text-secondary">Plan Name</small>
                    <strong className="text-end">
                      {planName || "!"}
                    </strong>
                  </div>

                  {!isTenantAdmin && (
                    <>
                      <div className="d-flex justify-content-between gap-3 mb-2">
                        <small className=" meta-sub-title text-secondary">Class</small>
                        <strong className="text-info text-end">
                          {isPublic ? "Public Mode" : isTemplate ? "Template Mode" : "Private Mode"}
                        </strong>
                      </div>

                      <div className="d-flex justify-content-between gap-3 mb-2">
                        <small className=" meta-sub-title text-secondary">Type</small>
                        <strong className="text-end">
                          {planType || "Professional Workspace"}
                        </strong>
                      </div>

                      <div className="d-flex justify-content-between gap-3 mb-2">
                        <small className=" meta-sub-title text-secondary">Duration</small>
                        <strong className="text-white text-end">
                          {trialDurationDays ? `${trialDurationDays} Days` : "No Trial"}
                        </strong>
                      </div>
                    </>
                  )}

                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <small className=" meta-sub-title text-secondary">Status</small>

                    <span
                      className={`badge rounded-pill px-3 py-2 ${isActive ? "text-bg-success" : "text-bg-secondary"
                        }`}
                    >
                      {isActive ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </div>
                </div>

                <div className="pbSummary__periodList">
                  {BILLING_PERIODS.map((p) => {
                    const isActive = billingPeriod === p.id;
                    const totalForPeriod = getPeriodTotal(p);
                    const isAnnual = p.months === 12;

                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={`pbPeriodCard position-relative overflow-hidden ${isActive ? "pbPeriodCard--active" : ""}`}
                        onClick={() => setBillingPeriod(p.id)}
                      >
                        {isActive &&
                          <div style={{ position: 'absolute', right: 10, top: 10, opacity: '0.6' }}>
                            <CheckIcon className="wf-icon text-white" />
                          </div>
                        }
                        <div className="pbPeriodCard__top">
                          <div className="pbPeriodCard__label">
                            {isAnnual ? "Annual Billing" : "Monthly Billing"}
                          </div>
                        </div>

                        <div className="pbPeriodCard__main">
                          <div className="pbPeriodCard__amount">
                            ${formatMoney(totalForPeriod)}
                          </div>
                          <div className="pbPeriodCard__amountSuffix">
                            / {isAnnual ? "yr" : "mo"}
                          </div>
                        </div>

                        <div className="pbPeriodCard__sub">
                          <span>
                            {isAnnual
                              ? `$${formatMoney(monthlyPrice)}/mo billed yearly`
                              : "Pay monthly, pause or scale anytime"}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="pbSummary__divider"></div>

                <div className="pbBreakdown">
                  <span className="fw-bold">COST BREAKDOWN</span>

                  {moduleCostBreakdown.length > 0 ? (
                    moduleCostBreakdown.map((module) => (
                      <div className="pbBreakdown__row" key={module.id}>
                        <span>
                          {module.name} ({module.featureCount} feature{module.featureCount !== 1 ? "s" : ""})
                          {module.resourceCount > 0 && `, ${module.resourceCount} resource${module.resourceCount !== 1 ? "s" : ""}`}
                        </span>
                        <strong>${formatMoney(module.totalCost)}</strong>
                      </div>
                    ))
                  ) : (
                    <div className="pbBreakdown__empty">No billable modules selected</div>
                  )}

                  {activeResourceBreakdown.length > 0 && (
                    <div className="pbBreakdown__resources">
                      <strong>Active Resource Sliders:</strong>
                      {activeResourceBreakdown.slice(0, 5).map((resource) => (
                        <div className="pbBreakdown__resourceRow" key={resource.id}>
                          <span>
                            {resource.name} ({resource.value} {resource.unit}) in {resource.moduleName}
                            {resource.adjustment.value > 0 && (
                              resource.adjustment.type === "discount"
                                ? ` - ${resource.adjustment.value}% off`
                                : ` + ${resource.adjustment.value}% margin`
                            )}
                          </span>
                          <b>${formatMoney(resource.cost)}</b>
                        </div>
                      ))}
                    </div>
                  )}
                </div>


                <div
                  className={`text-center mt-2 ${(submitError === zeroPriceWarningMessage || submitError === emptyPlanWarningMessage) ? "text-warning" : "text-danger"
                    }`}
                >
                  {submitError}
                </div>

                <button className="pbSummary__cta" onClick={handleFinalizeOffer} disabled={submitLoading}>
                  {submitLoading ? "Submitting..." : <>Create Plan</>}
                </button>


                <div className="pbSummary__finePrint text-center">
                  * Fill all required fields from schema to finalize.
                </div>
              </div>
            </div>
          </div>
        </div>


      </div>




      {confirmDelete.open && (
        <div className="center-form">
          <div style={{ maxWidth: "520px" }}>
            <div className="cf-head">
              <div>
                <h2>
                  Delete {confirmDelete.type === "feature" ? "Feature" : "Resource"}
                </h2>
              </div>
              <button
                className="cf-close text-secondary"
                onClick={() => {
                  setDeleteError(null);
                  setConfirmDelete({
                    open: false,
                    type: "",
                    id: null,
                    name: "",
                  });
                }}
              >
                ✕
              </button>
            </div>

            <div className="cf-body">
              <p className="fw-semibold text-danger mb-2">
                Are you sure you want to delete this {confirmDelete.type}?
              </p>

              <p className="text-secondary small-font mb-0">
                <strong>{confirmDelete.name}</strong> will be permanently removed.
                This action cannot be undone.
              </p>

              {deleteError && (
                <div className="mt-3 p-3 border border-danger text-danger" role="alert">
                  {deleteError}
                </div>
              )}
            </div>

            <div className="cf-footer d-a justify-content-between w-100">
              <button
                className="container-card px-4 py-2 text"
                onClick={() => {
                  setDeleteError(null);
                  setConfirmDelete({
                    open: false,
                    type: "",
                    id: null,
                    name: "",
                  });
                }}
              >
                CANCEL
              </button>

              <button
                className="cf-submit bg-danger px-4"
                onClick={handleConfirmDelete}
                disabled={deleteLoading || isDeleteBlocked}
              >
                {deleteLoading
                  ? "Deleting..."
                  : isDeleteBlocked
                    ? "Cannot Delete"
                    : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}


      <CustomFeatureModal
        showModal={showCustomFeature}
        entitlementId={formState.active_module_for_feature}
        editData={editFeatureData}
        isEdit={!!editFeatureData}
        featureOptions={FEATURES}
        resourceOptions={RESOURCES}
        onClose={() => {
          setShowCustomFeature(false);
          setEditFeatureData(null);
        }}
        onSubmit={() => {
          setShowCustomFeature(false);
          setEditFeatureData(null);
          refetchAllData();
        }}
      />

      <CustomResourceModal
        showModal={showCustomResource}
        entitlementId={formState.active_module_for_resource}
        editData={editResourceData}
        isEdit={!!editResourceData}
        onClose={() => {
          setShowCustomResource(false);
          setEditResourceData(null);
        }}
        onSubmit={() => {
          setShowCustomResource(false);
          setEditResourceData(null);
          refetchAllData();
        }}
      />


      <div className="mobile-view-bottom"></div>



    </>
  );
}

function PriceAdjustmentControl({ type, value, onTypeChange, onValueChange }) {
  const isMargin = type === "margin";

  return (
    <div className="cpDiscountControl">
      <label>Price Adjustment</label>
      <div className="cpDiscountControl__input">
        <select value={type} onChange={(event) => onTypeChange(event.target.value)}>
          <option value="discount">Discount</option>
          <option value="margin">Margin</option>
        </select>
        <input
          type="number"
          min={0}
          max={isMargin ? 1000 : 100}
          step={1}
          value={value}
          placeholder="0"
          onChange={(event) => onValueChange(event.target.value)}
        />
        <span>{isMargin ? "% margin" : "% off"}</span>
      </div>
    </div>
  );
}
